import { Injectable } from '@angular/core';
import { delay, forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import type { FavoritesClient, LoadFavoriteResult } from '../../../../core/tokens/favorites-client.token';
import type {
  IMatrixFavorite,
  IMatrixFavoriteCreate,
  IMatrixFavoriteColumnFilter,
} from '../../../models/favorite.model';
import type { IFilterOptions } from '../components/favorites-filters-dropdown/models/filter-options.model';
import {
  createFavoriteDto,
  mapFavoriteFiltersToFilterOptions,
  mapFavoriteFiltersToFiltersByTab,
  mapFavoritesToOptions,
  syncFavoriteFilters,
} from '../../../utils/favorite-functions.utils';

@Injectable({ providedIn: 'root' })
export class FavoritesMockService implements FavoritesClient {
  private mockFavorites: IMatrixFavorite[] = [
    {
      id: 1,
      username: 'testuser',
      matrixDashboardId: 1,
      name: 'My Default Filters',
      matrixFavoriteFilters: [
        {
          id: 1,
          matrixFavoriteId: 1,
          tabIndex: 0,
          filterKeyId: 1,
          filterKeyDescription: 'Medication',
          filterValue: 'Medication A,Medication B',
        },
        {
          id: 2,
          matrixFavoriteId: 1,
          tabIndex: 0,
          filterKeyId: 2,
          filterKeyDescription: 'Location',
          filterValue: 'Unit 1',
        },
      ],
      organization: {
        id: 1,
        name: 'Test Organization',
        guid: 'test-guid',
        midasAdminOrganizationPermissions: 0,
        cartAutoLogoutTime: 30,
        anytimeWasteNotificationTimePeriod: 60,
        anytimeTransportNotificationTimePeriod: 60,
        rxInterceptRuleNotificationTimePeriod: 60,
        rxInterceptPriorityToAssay: 0,
        enforceUniqueBagIdentifiers: false,
        alternateBinsOnORWaste: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        address: null,
        city: null,
        state: null,
        zip: null,
        contactName: null,
        contactPhone: null,
        contactEmail: null,
        accountNumber: 'TEST001',
        rootUser: {} as any,
        baselineScheduleI: 0,
        baselineScheduleII: 0,
        baselineScheduleIII: 0,
        baselineScheduleIV: 0,
        baselineScheduleV: 0,
        baselineNonControlled: 0,
        enforceBinExpiry: false,
        binExpiryDays: 0,
        patientRetentionLimit: 0,
        orderRetentionLimit: 0,
      },
      matrixDashboard: {
        id: 1,
        description: 'Home Dashboard',
      },
    },
  ];

  fetchFavoritesBYDashboardAndUsername(dashboardId: number): Observable<IMatrixFavorite[]> {
    const filtered = this.mockFavorites.filter((fav) => fav.matrixDashboardId === dashboardId);
    return of(filtered).pipe(delay(300));
  }

  fetchFavoriteById(favoriteId: number): Observable<IMatrixFavorite> {
    const favorite = this.mockFavorites.find((fav) => fav.id === favoriteId);
    if (!favorite) {
      throw new Error(`Favorite with id ${favoriteId} not found`);
    }
    return of(favorite).pipe(delay(300));
  }

  fetchFavoritesByUsername(): Observable<IMatrixFavorite[]> {
    return of([...this.mockFavorites]).pipe(delay(300));
  }

  createFavorite(favorite: IMatrixFavoriteCreate): Observable<IMatrixFavorite> {
    const newFavorite: IMatrixFavorite = {
      ...favorite,
      id: Math.max(...this.mockFavorites.map((f) => f.id || 0), 0) + 1,
      username: 'testuser',
      organization: {
        id: 1,
        name: 'Test Organization',
        guid: 'test-guid',
        midasAdminOrganizationPermissions: 0,
        cartAutoLogoutTime: 30,
        anytimeWasteNotificationTimePeriod: 60,
        anytimeTransportNotificationTimePeriod: 60,
        rxInterceptRuleNotificationTimePeriod: 60,
        rxInterceptPriorityToAssay: 0,
        enforceUniqueBagIdentifiers: false,
        alternateBinsOnORWaste: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        address: null,
        city: null,
        state: null,
        zip: null,
        contactName: null,
        contactPhone: null,
        contactEmail: null,
        accountNumber: 'TEST001',
        rootUser: {} as any,
        baselineScheduleI: 0,
        baselineScheduleII: 0,
        baselineScheduleIII: 0,
        baselineScheduleIV: 0,
        baselineScheduleV: 0,
        baselineNonControlled: 0,
        enforceBinExpiry: false,
        binExpiryDays: 0,
        patientRetentionLimit: 0,
        orderRetentionLimit: 0,
      },
      matrixDashboard: {
        id: favorite.matrixDashboardId,
        description: 'Dashboard',
      },
    };
    this.mockFavorites.push(newFavorite);
    return of(newFavorite).pipe(delay(300));
  }

  updateFavorite(favoriteId: number, payload: IMatrixFavorite): Observable<IMatrixFavorite> {
    const index = this.mockFavorites.findIndex((fav) => fav.id === favoriteId);
    if (index === -1) {
      throw new Error(`Favorite with id ${favoriteId} not found`);
    }
    const updated = { ...payload, id: favoriteId };
    this.mockFavorites[index] = updated;
    return of(updated).pipe(delay(300));
  }

  deleteFavorite(favoriteId: number): Observable<void> {
    const index = this.mockFavorites.findIndex((fav) => fav.id === favoriteId);
    if (index === -1) {
      throw new Error(`Favorite with id ${favoriteId} not found`);
    }
    this.mockFavorites.splice(index, 1);
    return of(undefined).pipe(delay(300));
  }

  loadFavoritesAsOptions(): Observable<ReturnType<typeof mapFavoritesToOptions>> {
    return this.fetchFavoritesByUsername().pipe(
      map((favorites) => mapFavoritesToOptions(favorites))
    );
  }

  loadFavoritesAsOptionsByDashboard(dashboardId: number): Observable<ReturnType<typeof mapFavoritesToOptions>> {
    return this.fetchFavoritesBYDashboardAndUsername(dashboardId).pipe(
      map((favorites) => mapFavoritesToOptions(favorites))
    );
  }

  loadFavoriteWithFilters(favoriteId: number): Observable<{
    favorite: IMatrixFavorite;
    filters: IFilterOptions;
    filtersByTab: IFilterOptions[];
    columnFilters: IMatrixFavoriteColumnFilter[];
  }> {
    return this.fetchFavoriteById(favoriteId).pipe(
      map((favorite) => ({
        favorite,
        filters: mapFavoriteFiltersToFilterOptions(favorite),
        filtersByTab: mapFavoriteFiltersToFiltersByTab(favorite.matrixFavoriteFilters || []),
        columnFilters: favorite.matrixFavoriteColumnFilters ?? [],
      }))
    );
  }

  loadFavoriteAndAllFavorites(favoriteId: number, username: string): Observable<LoadFavoriteResult> {
    return forkJoin({
      favorite: this.fetchFavoriteById(favoriteId),
      allFavorites: this.fetchFavoritesByUsername(),
    });
  }

  createFavoriteFromFilters(
    filters: Record<string, unknown>,
    dashboardId: number,
    favoriteName: string,
    organizationId: string,
    tabs?: { id: string }[],
    filtersConfig?: readonly { value: string }[],
    columnFilters?: IMatrixFavoriteColumnFilter[]
  ): Observable<IMatrixFavorite> {
    const payload = tabs?.length
      ? createFavoriteDto({
          dashboardId,
          favoriteName,
          organizationId,
          tabs,
          formValue: filters,
          filtersConfig,
          columnFilters,
        })
      : createFavoriteDto({
          filters: filters as IFilterOptions,
          dashboardId,
          favoriteName,
          organizationId,
          columnFilters,
        });
    return this.createFavorite(payload);
  }

  updateFavoriteWithFilters(
    favoriteId: number,
    currentFavorite: IMatrixFavorite,
    newFilters: Record<string, unknown>,
    tabs?: { id: string }[],
    columnFilters?: IMatrixFavoriteColumnFilter[]
  ): Observable<IMatrixFavorite> {
    const normalizedFilters: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(newFilters)) {
      if (key === 'savedFilters') continue;
      if (Array.isArray(value) && value.length && typeof value[0] === 'object') {
        const flattened = this.extractTreeValues(value as any[]);
        normalizedFilters[key] = flattened.join(',');
      } else {
        normalizedFilters[key] = value;
      }
    }
    const updatedFilters = syncFavoriteFilters(
      currentFavorite.matrixFavoriteFilters || [],
      normalizedFilters,
      favoriteId,
      tabs
    );
    const payload: IMatrixFavorite = {
      ...currentFavorite,
      matrixFavoriteFilters: updatedFilters,
      matrixFavoriteColumnFilters: columnFilters ?? currentFavorite.matrixFavoriteColumnFilters ?? [],
    };
    return this.updateFavorite(favoriteId, payload);
  }

  private extractTreeValues(nodes: any[]): string[] {
    const values: string[] = [];

    for (const node of nodes) {
      if (node?.value) {
        values.push(node.value);
      }

      if (Array.isArray(node.children)) {
        values.push(...this.extractTreeValues(node.children));
      }
    }

    return values;
  }
}
